import { useMemo } from 'react'
import { useTranslate } from 'src/localization/Trns'
import type { StatementDocumentFormat } from 'src/page-components/Accounts/AccountStatementsTab/accountStatementsDemoData'

/**
 * The one place a statement document format gets a human name (EX-40602).
 *
 * Shared between the export menu and the download failure copy on purpose: the
 * two used to name the same format differently, because the menu had a label
 * map and the error interpolated the raw wire enum. Picking "Excel (.xlsx)" and
 * being told "this statement isn't available as XLSX" names a token the user
 * never saw and cannot match to what they clicked.
 *
 * Exhaustive over the union rather than defaulted, so adding a contract format
 * fails typecheck here instead of leaking a SCREAMING_SNAKE token onto the page.
 */
export const useStatementDocumentFormatLabels = (): Record<
  StatementDocumentFormat,
  string
> => {
  const translate = useTranslate()

  // Memoized because a consumer holds it in a `useCallback` dependency list —
  // a fresh object every render would give those callbacks a new identity on
  // every render and make the memoization inert.
  return useMemo(
    () => ({
      PDF: translate('PDF'),
      XLSX: translate('Excel (.xlsx)'),
      CSV: translate('CSV'),
    }),
    [translate],
  )
}
